import { useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from '@/store'
import { checkLogin, getUserInfo, loginout, setUserInfo, updateAcceptMarketing } from '@/store/authSlice'
import { Message } from '@/components'
import { countryIs, countryVersion, COUNTRY_DOMAIN_MAP, exceptCountries } from '@/utils/countryVersion'
import { verifyLoginCode, verifyLoginTicket } from '@/services/api'
import { useUrlSearch } from '@/hooks'
import { shallowEqual } from 'react-redux'
import { adminLogin, hrefToOneClubUrl, OneClubPathMap, paramKey, replaceToOneClubUrl } from '@/utils/user'
import { useLanguage } from '@/hooks/useLanguage'
import { getConfig } from '@/store/configSlice'

export function useUserLogin() {
  const history = useHistory()
  const dispatch = useDispatch()
  const q = useUrlSearch()
  const info = useSelector((state) => state.auth.userInfo, shallowEqual)
  const { handleFixedLocalLanguage } = useLanguage()

  /**
   * 校验商家域名匹配
   */
  const matchCountry = useCallback(async () => {
    try {
      const [, res] = await Promise.all([dispatch(getConfig), dispatch(getUserInfo(true))])
      // 本地就别跳转了
      if (process.env.NODE_ENV === 'development') {
        handleFixedLocalLanguage(res.countryCode)
        return { isMatch: true }
      }
      if (countryIs.GLOBAL) {
        // 国际版
        // 所属国不在当前版本，跳到对应国家版本去
        if ((exceptCountries as string[]).includes(res.countryCode)) {
          const origin = COUNTRY_DOMAIN_MAP[res.countryCode || 'CN']
          const url = location.href.replace(location.origin, origin).concat(`${!!location.search ? '&' : '?'}loginCode=${res?.code}`)
          dispatch(setUserInfo(null))
          window.location.replace(url)
          return Promise.reject(false)
        }
      } else {
        // 本土站点版
        if (res.countryCode !== countryVersion) {
          // 如果也不属于当前版本，跳去无权限
          dispatch(setUserInfo(null))
          const search = q?.lang ? `?lang=${q?.lang}` : ''
          history.push(`/authority${search}`)
          return Promise.reject(false)
        }
      }
      handleFixedLocalLanguage(res.countryCode)
      return { isMatch: true }
    } catch (error) {
      Message.error(error)
      return Promise.reject(false)
    }
  }, [dispatch, handleFixedLocalLanguage, history, q?.lang])

  /**
   * 登录成功
   */
  const loginSuccessCb = useCallback(async () => {
    // 更新登录状态
    await dispatch(checkLogin())

    // 国家版本
    await matchCountry()
  }, [dispatch, matchCountry])

  /** code登陆 */
  const verifyCode = useCallback(
    async (code) => {
      try {
        await verifyLoginCode({ code, appid: process.env.APPID, subappid: process.env.SUB_APPID })
        await loginSuccessCb()
      } catch (error) {
        throw error
      }
    },
    [loginSuccessCb]
  )
  /** ticket登陆 */
  const verifyTicket = useCallback(
    async (ticket, acceptMarketing?: string) => {
      try {
        await verifyLoginTicket({ ticket, appid: process.env.APPID, subappid: process.env.SUB_APPID })
        // 登录更新用户信息
        if (typeof acceptMarketing === 'string') {
          await updateAcceptMarketing(acceptMarketing === '1')
        }

        await loginSuccessCb()
      } catch (error) {
        if (error?.rescode === '1028') {
          hrefToOneClubUrl(OneClubPathMap.login, 'expired')
        }
        throw error
      }
    },
    [loginSuccessCb]
  )
  /** 登录流程 */
  const userLogin = useCallback(async () => {
    try {
      adminLogin()
      const { isLogined } = await dispatch(checkLogin())
      // code||ticket登陆
      if (q?.loginCode || q?.ticket) {
        // 删除登录参数
        const url = new URL(window.location.href)
        paramKey.forEach((item) => url.searchParams.delete(item))
        if (q?.lang) url.searchParams.delete('lang')
        window.history.replaceState(null, null, url)
        if (q.loginCode) {
          await verifyCode(q.loginCode)
        } else if (q?.ticket) {
          await verifyTicket(q.ticket, q?.acceptMarketing)
        }
        return
      }
      if (!isLogined) {
        replaceToOneClubUrl(OneClubPathMap.login)
        return Promise.reject()
      } else if (isLogined && !info?.uid) {
        // 重新校验国家
        await matchCountry()
      }
    } catch (error) {
      return Promise.reject(error)
    }
  }, [dispatch, info?.uid, matchCountry, q?.lang, q.loginCode, q.ticket, q?.acceptMarketing, verifyCode, verifyTicket])

  /** 登出并跳转到oneclub */
  const logoutToOnclub = useCallback(
    async (scene?: string) => {
      await dispatch(loginout())
      hrefToOneClubUrl(OneClubPathMap.login, scene || 'logout')
    },
    [dispatch]
  )

  return {
    matchCountry,
    loginSuccessCb,
    verifyCode,
    logoutToOnclub,
    userLogin,
    verifyTicket,
  }
}
